export type BlogMainCategory =
	| 'Audio'
	| 'How to Live Stream'
	| 'Strategy'
	| 'Tips'
	| 'Tutorials';

type BlogSubCategory =
	| 'Branding'
	| 'Business'
	| 'Equipment'
	| 'Faith'
	| 'Guides'
	| 'Production'
	| 'Setup';

export type BlogCategory = BlogMainCategory | BlogSubCategory;

// If a value is undefined it will show 'All' as active in the header
const categoryToMainCategory: {
	[key in BlogCategory]: BlogMainCategory | undefined;
} = {
	Audio: 'Audio',
	Branding: 'Tutorials',
	Business: 'Tutorials',
	Equipment: 'Tips',
	Faith: 'How to Live Stream',
	Guides: 'Tips',
	'How to Live Stream': 'How to Live Stream',
	Production: 'How to Live Stream',
	Setup: 'How to Live Stream',
	Strategy: 'Strategy',
	Tips: 'Tips',
	Tutorials: 'Tutorials',
};

export type BlogCategoryUrl =
	| 'faith-based-live-streaming'
	| 'how-to-live-stream'
	| 'live-stream-branding'
	| 'live-stream-business'
	| 'live-stream-production'
	| 'live-streaming-audio'
	| 'live-streaming-equipment'
	| 'live-streaming-guides'
	| 'live-streaming-setup'
	| 'live-streaming-strategy'
	| 'live-streaming-tips'
	| 'live-streaming-tutorials';

const categoryTypeToUrlMap: {
	[key in BlogCategory]: BlogCategoryUrl;
} = {
	Audio: 'live-streaming-audio',
	Branding: 'live-stream-branding',
	Business: 'live-stream-business',
	Equipment: 'live-streaming-equipment',
	Faith: 'faith-based-live-streaming',
	Guides: 'live-streaming-guides',
	'How to Live Stream': 'how-to-live-stream',
	Production: 'live-stream-production',
	Setup: 'live-streaming-setup',
	Strategy: 'live-streaming-strategy',
	Tips: 'live-streaming-tips',
	Tutorials: 'live-streaming-tutorials',
} as const;

export const getBlogCategory = (categoryUrl?: string) => {
	return Object.keys(categoryTypeToUrlMap).find(
		(key) => categoryTypeToUrlMap[key as BlogCategory] === categoryUrl,
	) as BlogCategory | undefined;
};

export const getCategoryUrl = (category?: string) => {
	return categoryTypeToUrlMap[category as BlogCategory] as
		| BlogCategoryUrl
		| undefined;
};

export const getBlogMainCategory = (category?: string) => {
	return categoryToMainCategory[category as BlogCategory] as
		| BlogMainCategory
		| undefined;
};

export const getAllBlogCategoriesForMainCategory = (mc: BlogMainCategory) => {
	const categories = Object.entries(categoryToMainCategory).reduce(
		(acc, current) => {
			const [category, mainCategory] = current;
			if (mc === mainCategory) {
				acc.push(category as BlogCategory);
			}
			return acc;
		},
		[] as BlogCategory[],
	);
	return categories;
};

type CategoryWithTranslationType = {
	slug: string;
	name: string;
	translationKey: string;
};

export const categoriesWithTranslations: {
	[key: string]: CategoryWithTranslationType;
} = {
	[categoryTypeToUrlMap['How to Live Stream']]: {
		slug: categoryTypeToUrlMap['How to Live Stream'],
		name: 'How to Live Stream',
		translationKey: 'blog.category.howToLiveStream',
	},
	[categoryTypeToUrlMap.Strategy]: {
		slug: categoryTypeToUrlMap.Strategy,
		name: 'Strategy',
		translationKey: 'blog.category.strategy',
	},
	[categoryTypeToUrlMap.Audio]: {
		slug: categoryTypeToUrlMap.Audio,
		name: 'Audio',
		translationKey: 'blog.category.audio',
	},
	[categoryTypeToUrlMap.Tutorials]: {
		slug: categoryTypeToUrlMap.Tutorials,
		name: 'Tutorials',
		translationKey: 'blog.category.tutorials',
	},
	[categoryTypeToUrlMap.Tips]: {
		slug: categoryTypeToUrlMap.Tips,
		name: 'Tips',
		translationKey: 'blog.category.tips',
	},
};

export const allCategoryLinks = [
	{
		slug: '',
		name: 'All',
		translationKey: 'blog.category.all',
	},
	...Object.values(categoriesWithTranslations),
];
